import {Component, Inject, OnInit} from '@angular/core';
import {ReferenceService} from '../../../services/reference.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ApplicationReferenceLinkDTO} from '../../../models/application/ApplicationReferenceLinkDTO';
import {take} from 'rxjs/operators';
import {forkJoin} from 'rxjs';
import {CreateNameValueDTO, NameValueDTO} from '../../../../common/models/NameValueDTO';
import {ReferenceDTO} from '../../../models/application/ReferenceDTO';
import {KeyValueMapping} from '../../../../common/types/common.types';
import {ReferenceLookupValue} from 'src/app/modules/clm/types/reference-lookup-value.enum';

interface DialogData {
  applicationId: number;
  selectedIndicators: ApplicationReferenceLinkDTO[];
  createdBy: string;
}

@Component({
  selector: 'greydom-application-indicator',
  templateUrl: './application-indicator.component.html',
  styleUrls: []
})
export class ApplicationIndicatorComponent implements OnInit {

  form: FormGroup;
  ntuReasons: NameValueDTO[] = [];
  ntuReasonReferences: ReferenceDTO[] = [];
  selectedNtuReason: NameValueDTO;
  missingInformation: string[] = [];
  missingInformationReferences: ReferenceDTO[] = [];
  selectedMissingInformationList: KeyValueMapping<any> = {};
  isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ApplicationIndicatorComponent>,
    private referenceService: ReferenceService
  ) {
    this.selectedNtuReason = this.getSelectedNtuReason();
    this.selectedMissingInformationList = this.getSelectedMissingInformationList();
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.initReferenceValues();
  }

  onNtuReasonSelected(selectedOption: NameValueDTO): void {
    this.form.get('ntuReason').setValue(selectedOption);
  }

  onMissingInformationSelectionChanged(selectedIndicators: KeyValueMapping<any>): void {
    this.form.get('missingInformation').setValue(selectedIndicators);
  }

  onClickCancel(): void {
    this.dialogRef.close();
  }

  onClickSubmit(): void {
    if (this.form.valid) {
      this.data.selectedIndicators = [];
      const selectedNtuReasonReferenceLink = this.getSelectedNtuReasonReferenceLink();
      if (selectedNtuReasonReferenceLink != null) {
        this.data.selectedIndicators.push(selectedNtuReasonReferenceLink);
      }
      const selectedMissingInformationReferenceLinks = this.getSelectedMissingInformationReferenceLinks();
      if (selectedMissingInformationReferenceLinks != null) {
        this.data.selectedIndicators.push(...selectedMissingInformationReferenceLinks);
      }
      this.dialogRef.close(this.data.selectedIndicators);
    }
  }

  private initFormGroup(): void {
    this.form = this.formBuilder.group({
      ntuReason: [this.selectedNtuReason, Validators.required],
      missingInformation: [this.selectedMissingInformationList]
    });
  }

  private initReferenceValues(): void {
    forkJoin({
      ntuReasons: this.referenceService.getAllByFieldName(ReferenceLookupValue.NtuReason).pipe(take(1)),
      missingInformation: this.referenceService.getAllByFieldName(ReferenceLookupValue.MissingInformation).pipe(take(1))
    })
      .subscribe({
        next: ({ ntuReasons, missingInformation }) => {
          this.ntuReasonReferences = ntuReasons;
          this.ntuReasons = ntuReasons.map(
            (reference) => CreateNameValueDTO(reference.referenceId, reference.value)
          );
          this.missingInformationReferences = missingInformation;
          this.missingInformation = missingInformation.map(reference => reference.value);
        },
        error: (error) => {
          console.error('Error loading references:', error);
        }
      });
  }

  private getSelectedNtuReason(): NameValueDTO | null {
    const selectedReferenceLink = this.data.selectedIndicators?.find(
      reference => reference.reference?.fieldName === 'ntuReason'
    );
    return selectedReferenceLink
      ? CreateNameValueDTO(selectedReferenceLink.reference.referenceId, selectedReferenceLink.reference.value)
      : null;
  }

  private getSelectedMissingInformationList(): KeyValueMapping<any> | null {
    const selectedReferenceLinks = this.data.selectedIndicators?.filter(
      reference => reference.reference?.fieldName === 'missingInformation'
    );

    return selectedReferenceLinks?.length > 0
      ? selectedReferenceLinks.reduce((acc, curr) => {
        acc[curr.reference.value] = true;
        return acc;
      }, {} as Record<string, boolean>)
      : null;
  }

  private getSelectedNtuReasonReferenceLink(): ApplicationReferenceLinkDTO {
    if (!this.ntuReasonReferences) {
      return null;
    }
    const reference = this.ntuReasonReferences.find(x => x.referenceId == this.form.get('ntuReason').value?.value);
    return this.getSelectedReferenceLink(reference);
  }

  private getSelectedMissingInformationReferenceLinks(): ApplicationReferenceLinkDTO[] {
    if (!this.missingInformationReferences) {
      return [];
    }

    const missingInformationObject = this.form.get('missingInformation').value;
    if (!missingInformationObject) {
      return [];
    }

    const missingInformationStringList: string[] = Object.keys(missingInformationObject).filter(
      key => missingInformationObject[key] === true
    );

    const selectedLinks: ApplicationReferenceLinkDTO[] = missingInformationStringList
      ?.map(stringValue => {
        const reference = this.missingInformationReferences.find(x => x.value === stringValue);
        return reference ? this.getSelectedReferenceLink(reference) : null;
      })
      .filter(link => link !== null);

    return selectedLinks;
  }

  private getSelectedReferenceLink(selectedReference: ReferenceDTO) {
    const selectedReferenceLink = new ApplicationReferenceLinkDTO();
    const date = new Date();
    selectedReferenceLink.applicationId = this.data.applicationId;
    selectedReferenceLink.reference = selectedReference;
    selectedReferenceLink.startDate = date;
    selectedReferenceLink.createdBy = this.data.createdBy;
    selectedReferenceLink.created = date;
    return selectedReferenceLink;
  }
}
